import _ from 'lodash';
import { useQuery } from '@tanstack/react-query';
import QUERY_KEYS from 'services/api/queryKeys';
import {
  PaginationType,
  ParamsGetTransactionsType,
  PendingPostRoomCharge,
  TransactionCodesResponse,
  TransactionData,
  TransactionPayment
} from 'services/api/type/booking.type';
import { axiosInstance } from 'services/api/base.api';
import {
  getAddonServiceTaxes,
  getPendingPostRoomCharge,
  transactionCode
} from 'services/api/module/booking.api';
import { useMemo, useState, useEffect } from 'react';
import { generateUniqueId } from 'utils';
import { paths } from 'services/api/paths';

const getPartyId = (booking: any) => {
  if (booking.moved_booking_id) return booking.moved_booking_id;
  if (booking.tour_info) return booking.tour_info;
  if (booking.group_id) return '';
  return 'FIT';
};

const shouldCreateNewParty = (lastItem: any, booking: any) => {
  if (!lastItem) return true;

  return (
    lastItem.moved_booking_id !== booking.moved_booking_id ||
    lastItem.group_id !== booking.group_id ||
    lastItem.tour_info !== booking.tour_info
  );
};

const sortBookings = (a: any, b: any) => {
  const aId = a.moved_booking_id || a.booking_id;
  const bId = b.moved_booking_id || b.booking_id;
  return bId - aId; // Sort in descending order
};

const groupByParty = (data: any) => {
  return (data || []).reduce((acc: any, booking: any) => {
    const lastItem = acc[acc.length - 1];

    if (shouldCreateNewParty(lastItem, booking)) {
      // Create new party group header
      acc.push({
        is_party: true,
        party_id: getPartyId(booking),
        group_id: booking.group_id,
        group_name: booking.group_name,
        tour_info: booking.tour_info,
        booking_count: 0, // Initialize booking count
        id: generateUniqueId()
      });
    }

    // Increment booking count for the current party group
    const currentPartyIndex = acc.findLastIndex((item: any) => item.is_party);
    if (currentPartyIndex !== -1) {
      acc[currentPartyIndex].booking_count += 1;
    }

    // Push the booking itself
    acc.push(booking);

    return acc;
  }, []);
};

export const useGetGuestTransactions = (
  filterOptions?: ParamsGetTransactionsType,
  page: number = 1,
  search?: string,
  limit: number = 40
) => {
  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEYS.GET_GUEST, page, search, limit, filterOptions],
    queryFn: () =>
      axiosInstance
        .get('/guests', {
          params: {
            ...filterOptions,
            page,
            limit
          }
        })
        .then(item => item.data)
  });

  const pagination = _.get(data, 'pagination', {}) as PaginationType;
  const hasMore = pagination.total > page * limit;
  const [finalData, setFinalData] = useState<any>([]);

  useEffect(() => {
    if (!data?.data) return;

    const sortedData = data.data.sort(sortBookings);
    const groupedData = groupByParty(sortedData);

    if (page === 1) {
      setFinalData(groupedData);
    } else {
      setFinalData((prevData: any) => [...prevData, ...groupedData]);
    }
  }, [data, page]);

  return {
    data: finalData || [],
    pagination,
    isLoading,
    hasMore
  };
};

export const useGetGuestOrders = (guest_id: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, guest_id],
    queryFn: () => axiosInstance.get(`/guests/${guest_id}/orders`).then(item => item.data?.data),
    enabled: !!guest_id
  });
};

export const useGetGuestOrderDetail = (order_id?: number) => {
  return useQuery<TransactionData>({
    queryKey: [
      QUERY_KEYS.GET_GUEST_TRANSACTIONS,
      QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL,
      order_id
    ],
    queryFn: () => axiosInstance.get(`/guests/orders/${order_id}`).then(item => item.data?.data),
    enabled: !!order_id
  });
};

export const useGetTransactionCodes = (payment_type?: string, enable: boolean = true) => {
  return useQuery<TransactionCodesResponse[]>({
    queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL, enable],
    queryFn: () => transactionCode({ payment_type }),
    refetchOnWindowFocus: false
  });
};

export const usePendingPostRoomCharge = (filterOptions: any) => {
  const {
    data: dataPendingPostRoomCharge,
    status,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_BOOKING_LIST_ALL, filterOptions],
    queryFn: () => getPendingPostRoomCharge(filterOptions),
    enabled: !!filterOptions
  });

  const sortedData = useMemo(() => {
    const pendingPostRoomChargeData: PendingPostRoomCharge[] = _.get(
      dataPendingPostRoomCharge,
      'data.data',
      []
    );
    return pendingPostRoomChargeData.sort((a: any, b: any) => b.booking_id - a.booking_id);
  }, [dataPendingPostRoomCharge]);

  return {
    data: sortedData,
    status,
    isLoading,
    isFetching
  };
};

export const useGetAddonServiceTaxes = () => {
  const { data } = useQuery({
    queryKey: [QUERY_KEYS.GET_BOOKING_LIST_ALL],
    queryFn: () => getAddonServiceTaxes(),
    refetchOnWindowFocus: false
  });
  return {
    data: data
  };
};

export const useGetTransactionsFromOrders = (
  booking_line_guest_ids?: string | undefined,
  booking_line_order_id?: number
) => {
  const { data } = useQuery<TransactionPayment[]>({
    queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, booking_line_guest_ids, booking_line_order_id],
    queryFn: () =>
      axiosInstance
        .get(paths.getTransactionsFromOrders(), {
          params: { booking_line_guest_ids, booking_line_order_id }
        })
        .then(item => item.data?.data),
    enabled: !!booking_line_guest_ids
  });
  return {
    data: data
  };
};

export const useGetMinvoicePreview = (order_id: number, enabled: boolean) => {
  const { data, isFetching } = useQuery({
    queryKey: [QUERY_KEYS.GET_MINVOICE_PREVIEW, order_id],
    queryFn: () =>
      axiosInstance.get(paths.getMinvoicePreview(order_id)).then(item => item.data.data),
    enabled: !!order_id && enabled
  });
  return {
    data: data,
    isFetching: isFetching
  };
};

export const useGetTransactionDetail = (transactionId: number) => {
  const { data, isFetching } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_GUEST_TRANSACTIONS,
      QUERY_KEYS.GET_GUEST_TRANSACTIONS_DETAIL,
      transactionId
    ],
    queryFn: () =>
      axiosInstance.get(paths.getTransactionDetail(transactionId)).then(item => item.data.data),
    enabled: !!transactionId
  });
  return {
    data: data,
    isFetching: isFetching
  };
};
